/**
 * Parse and inject Contentful Embed Panel "Script Content" (DE-795).
 *
 * Inline script text cannot use `script.textContent` — CSP `script-src` has no
 * `'unsafe-inline'`. Load those bodies as `blob:` URLs instead. External
 * `<script src>` tags are unchanged (host must already be on the CSP allowlist).
 */

/**
 * @typedef {object} EmbedPanelScript
 * @property {'inline'|'external'} kind
 * @property {string} [code]
 * @property {string} [src]
 * @property {Record<string, string>} attributes
 */

const SCRIPT_TAG_RE = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;

const NAMED_HTML_ENTITIES = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  quot: '"',
};

/**
 * Decode HTML entities in attribute values.
 * Regex parsing does not do this; `innerHTML` used to.
 *
 * @param {string} value
 * @returns {string}
 */
export function decodeHtmlEntities(value) {
  if (!value) {
    return value;
  }

  return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, body) => {
    const token = body.toLowerCase();

    if (token.startsWith('#x')) {
      const code = parseInt(token.slice(2), 16);

      return Number.isFinite(code) ? String.fromCodePoint(code) : entity;
    }

    if (token.startsWith('#')) {
      const code = Number(token.slice(1));

      return Number.isFinite(code) ? String.fromCodePoint(code) : entity;
    }

    return Object.prototype.hasOwnProperty.call(NAMED_HTML_ENTITIES, token) ?
      NAMED_HTML_ENTITIES[token] :
      entity;
  });
}

/**
 * @param {string} attrString
 * @returns {Record<string, string>}
 */
export function parseScriptTagAttributes(attrString) {
  const attrs = {};

  if (!attrString) {
    return attrs;
  }

  const attrRe = /([^\s=]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|(\S+)))?/g;
  let match;

  while ((match = attrRe.exec(attrString))) {
    const name = match[1];

    if (!name) continue;

    attrs[name.toLowerCase()] = decodeHtmlEntities(match[2] ?? match[3] ?? match[4] ?? '');
  }

  return attrs;
}

/**
 * @param {unknown} scriptContent
 * @returns {EmbedPanelScript[]}
 */
export function parseEmbedPanelScripts(scriptContent) {
  if (scriptContent == null) {
    return [];
  }

  const html = String(scriptContent);
  const trimmed = html.trim();

  if (!trimmed) {
    return [];
  }

  const entries = [];
  let sawScriptTag = false;

  for (const match of trimmed.matchAll(SCRIPT_TAG_RE)) {
    sawScriptTag = true;
    const attributes = parseScriptTagAttributes(match[1]);
    const src = typeof attributes.src === 'string' ? attributes.src.trim() : '';

    if (src) {
      entries.push({kind: 'external', src, attributes});
      continue;
    }

    const code = match[2] ?? '';

    if (!code.trim()) {
      continue;
    }

    entries.push({kind: 'inline', code, attributes});
  }

  if (sawScriptTag) {
    return entries;
  }

  // Merch sometimes pastes a raw JS body with no wrapping <script> tags.
  return [{kind: 'inline', code: trimmed, attributes: {}}];
}

/**
 * Insert parsed scripts into `container`. Returns a cleanup that revokes any
 * remaining blob URLs (onload also revokes each blob — DE-795 / Vasyl).
 *
 * `document.createElement('script')` defaults `async` to true. Set `async = false`
 * on every tag *before* assigning `src` so mixed external + inline snippets keep
 * document order (`<script src="widget.js"></script><script>Widget.init()</script>`).
 *
 * @param {ParentNode} container
 * @param {unknown} scriptContent
 * @returns {() => void}
 */
export function injectEmbedPanelScripts(container, scriptContent) {
  const objectUrls = [];
  const entries = parseEmbedPanelScripts(scriptContent);

  for (const entry of entries) {
    const script = document.createElement('script');

    // Must run before `src` is set — too late after append.
    script.async = false;

    for (const [name, value] of Object.entries(entry.attributes)) {
      if (name === 'src') continue;
      script.setAttribute(name, value);
    }

    if (entry.kind === 'external') {
      script.src = entry.src;
    } else {
      const blob = new Blob([entry.code], {type: 'text/javascript'});
      const url = URL.createObjectURL(blob);

      objectUrls.push(url);
      script.src = url;
      script.onload = () => URL.revokeObjectURL(url);
      script.onerror = () => URL.revokeObjectURL(url);
    }

    container.appendChild(script);
  }

  return () => {
    for (const url of objectUrls) {
      URL.revokeObjectURL(url);
    }
  };
}
